import React, {useCallback, useEffect, KeyboardEvent as ReactKeyboardEvent} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import debounce from 'lodash.debounce';
import {defaultGridMinHeight, defaultRowsNum, headerHeight, rowHeight} from '../../consts/grid';
import {Table} from '../Table';
import {formsStore} from '../../../../stores/Form/FormsStore';
import {Column} from '../../../../stores/Form/Column';
import {Form} from '../../../../stores/Form/Form';
import {isRTL} from '../../../../Localization';
import {HeaderCellContent} from '../HeaderCellContent';
import {OpenTableMethods} from '../Table/types';
import {activateCurEntityBySession} from '../../../../_shared/GWTBridge/invoke/genPage';
import {CellContent} from '../CellContent';
import {cx_menu_input_anchor} from '../FormInput/constants';
import {Splitter} from '../Spitter';
import {SuspendedJobTypes} from '../../consts/suspendedJobTypes';
import {prefetchSubFormDataOnRowChange, updateTableHeight} from '../../actions/form';
import {isNavigationLocked} from '../../utils/isNavigationLocked';
import {keys} from '../../../../constants/KeyCodes';
import {EmptyState} from '../EmptyState';
import {openRecordInFocusView} from '../FocusViewDialog/actions';
import {handleNavigation} from './utils/handle-navigation';
import {useMouseWheelNav} from './hooks/useMouseWheelNav';
import {useCellResizer} from './hooks/useCellResizer';
import styles from '../../styles/grid.module.scss';

type Props = {
    currentForm: Form;
    isSubForm?: boolean;
    isApiLock: () => boolean;
    openSelectListModal: (lisType) => void;
    isActiveForm: boolean;
    defaultHeight?: number;
    isEmptyForm?: boolean;
    openNewEmptyRecord?: () => void;
};

const emptyRecords = [];
const rtl = isRTL();

export const Grid = observer(
    ({
        currentForm,
        openSelectListModal,
        isActiveForm,
        defaultHeight = defaultGridMinHeight,
        isEmptyForm,
        openNewEmptyRecord
    }: Props) => {
        const {currentScreen} = formsStore;
        const searchMode = currentForm?.searchModeLevel;
        const tableAPI = currentForm?.tableAPI;
        const records = !searchMode ? currentForm.records : emptyRecords;
        const isSubForm = currentForm?.level > 0;

        useEffect(() => {
            const macro = formsStore.recordingMacro;
            if (macro?.eventArray[macro?.eventArray.length - 1]?.type !== SuspendedJobTypes.Zoom) {
                formsStore.setEventForRecordingMacro({
                    type: SuspendedJobTypes.StartEntity,
                    formName: currentForm.ename
                });
            }
        }, [currentForm.ename]);

        const hasActiveSubForm = useCallback(() => {
            return currentForm?.level < formsStore.getActiveForm()?.level;
        }, [currentForm]);

        const debouncedPrefetchSubFormData = useCallback(
            debounce(async (form: Form, rowIndex: number) => {
                await prefetchSubFormDataOnRowChange(form, rowIndex);
            }, (formsStore.sysConstants?.activedelay?.Int as number) * 1000),
            [formsStore.sysConstants?.activedelay?.Int]
        );

        const onFocusCell = useCallback(
            async (currentCellIndex: number, rowIndex?: number) => {
                if (currentForm && formsStore.onBackground) {
                    activateCurEntityBySession(currentForm.session);
                }
                if (currentForm?.currentColumn) {
                    if (
                        currentForm.currentColumn.index !== currentCellIndex &&
                        currentForm.currentColumn.maxWidthMode
                    ) {
                        currentForm.currentColumn.setMaxWidthMode(false);
                        tableAPI?.recalcColumnStyles();
                    }
                    const oldRowIndex = currentForm.currentRow;
                    currentForm.setCurrentColumnIdByIndex(currentCellIndex);
                    currentForm.setCurrentRow(rowIndex);

                    const macro = formsStore.recordingMacro;
                    if (
                        macro?.eventArray[macro?.eventArray.length - 1]?.type ===
                        SuspendedJobTypes.Click
                    ) {
                        formsStore.setEventForRecordingMacro({
                            type: SuspendedJobTypes.FormGoto,
                            rowIdx: rowIndex,
                            colIdx: currentCellIndex,
                            formName: currentForm.ename
                        });
                    }
                    const activeSubForm = currentForm.activeSubForm;

                    if (activeSubForm && currentForm.currentRow !== oldRowIndex) {
                        activeSubForm.resetRecords();
                        if (activeSubForm.form.Ishtml) {
                            activeSubForm.setOriginHTMLContent('');
                        }
                        if (formsStore.sysConstants?.activedelay?.Int) {
                            debouncedPrefetchSubFormData(currentForm, currentForm.currentRow);
                        }
                    }
                }
            },
            [currentForm, tableAPI, debouncedPrefetchSubFormData]
        );

        const onReady = useCallback(
            (openMethods: OpenTableMethods) => {
                currentForm.setTableAPI(openMethods);
            },
            [currentForm]
        );

        const onDestroy = useCallback(() => currentForm.setTableAPI(null), [currentForm]);

        const checkRecordStatusAndSave = useCallback(async () => {
            const {onFieldFocusChange, handleFormLevel} = formsStore.currentScreen;

            if (isActiveForm) {
                const newCellIndex = currentForm.currentColumn?.index || 0;
                const newRowIndex = currentForm.currentRow + 1;

                await onFieldFocusChange(
                    {newCellIndex, newRowIndex, level: currentForm.level},
                    () => {
                        if (!currentForm.records[currentForm.currentRow]?.isTemporary) {
                            if (currentForm.records[newRowIndex]) {
                                tableAPI?.focusCell(newCellIndex, newRowIndex);
                            }
                        }
                    }
                );
            } else {
                await handleFormLevel(currentForm.level);
            }
        }, [currentForm, isActiveForm, tableAPI]);

        const beforeFocusCellChange = useCallback(
            async (
                cellIndex: number,
                rowIndex: number,
                action: (cellIndex: number, rowIndex: number) => void
            ) => {
                const payload = {
                    newCellIndex: cellIndex,
                    newRowIndex: rowIndex,
                    level: currentForm.level
                };
                await currentScreen.onFieldFocusChange(payload, action);
            },
            [currentScreen, currentForm.level]
        );

        const cellRenderer = useCallback(
            params => {
                const {rowData, column, rowIndex, isActiveCell} = params;
                const searchValue = currentForm.searchModeData[rowIndex]?.[column.id];
                const currField = rowData?.getField(column.id);
                return (
                    <CellContent
                        onBlur={debouncedPrefetchSubFormData?.cancel}
                        isActiveCell={isActiveCell}
                        rowIndex={rowIndex}
                        column={column}
                        searchMode={searchMode}
                        onMaxWidthMode={tableAPI?.recalcColumnStyles}
                        onInputFocus={currentForm.setFormActiveElement}
                        updateSearchValue={currentForm.updateSearchModeCellValue}
                        currField={currField}
                        searchValue={searchValue}
                        openSelectListModal={openSelectListModal}
                        openRecordInFocusView={openRecordInFocusView}
                        isCurrentRowActive={rowIndex === currentForm.currentRow}
                    />
                );
            },
            [searchMode, tableAPI, currentForm, openSelectListModal, debouncedPrefetchSubFormData]
        );

        const columns = currentForm.visibleColumns;
        const tableId = currentForm.session;

        const onColumnsPin = useCallback(
            async (count: number) => {
                await currentForm?.updateFixedColumnsCount(count);
            },
            [currentForm]
        );

        const headerRenderer = useCallback(
            (column: Column, pinned) => {
                const isSortedColumn = column.parentForm.sort.find(
                    item => Number(item.ColumnID) === column.id
                );
                return (
                    <HeaderCellContent
                        isActiveForm={isActiveForm}
                        column={column}
                        pinned={pinned}
                        onColumnsPin={onColumnsPin}
                        tableId={tableId}
                        isSorted={!!isSortedColumn}
                    />
                );
            },
            [onColumnsPin, tableId, isActiveForm]
        );

        const emptyStateRenderer = useCallback(() => {
            return <EmptyState form={currentForm} createNewRow={openNewEmptyRecord} />;
        }, [currentForm, openNewEmptyRecord]);

        const handleHeightResize = useCallback(
            async (height: number) => {
                await updateTableHeight(currentForm, height);
            },
            [currentForm]
        );

        const onScroll = useCallback((left: number, top: number) => {
            currentForm.tableScrollOffset.top = top;
            currentForm.tableScrollOffset.left = left;
        }, []);

        const onTableBodyClick = useCallback(
            async (e: React.MouseEvent<HTMLDivElement>) => {
                if ((e.target as HTMLDivElement).dataset.id === 'pui-table-body') {
                    await checkRecordStatusAndSave();
                }
            },
            [checkRecordStatusAndSave]
        );

        const isNavLocked = isNavigationLocked(currentForm);

        const onTableKeydown = useCallback(
            async (e: ReactKeyboardEvent<HTMLDivElement>) => {
                const {records} = currentForm;

                if (!isNavLocked && records.length === 1 && !searchMode) {
                    if (e.key === keys.ARROW_DOWN || e.key === keys.ARROW_UP) {
                        e.preventDefault();
                        e.stopPropagation();
                        await checkRecordStatusAndSave();
                        return;
                    }
                }
            },
            [currentForm, checkRecordStatusAndSave, searchMode]
        );

        const onKeyboardNavigation = useCallback(
            (e: KeyboardEvent, params) => {
                handleNavigation(e, currentForm, params);
            },
            [currentForm]
        );

        const onMouseWheel = useMouseWheelNav(currentForm);
        const onCellResizeEnd = useCellResizer(currentForm);

        const isParentInSearchMode = currentForm.parentForm?.searchModeLevel > 0;

        const pageRowsCount =
            searchMode || isParentInSearchMode
                ? Math.max(Math.ceil(defaultHeight / rowHeight), defaultRowsNum)
                : records.length;

        return (
            <Splitter
                resizable={!isEmptyForm}
                onResizeEnd={handleHeightResize}
                defaultHeight={defaultHeight}
                disabled={searchMode > 0 || isParentInSearchMode}
                minHeight={defaultGridMinHeight}
            >
                <Table
                    key={currentForm.key}
                    rounded={currentForm.level === 0}
                    blockedNavigation={isNavLocked}
                    isActive={isActiveForm}
                    id={tableId}
                    isRtl={rtl}
                    highlightCurrentRow={hasActiveSubForm()}
                    fixedColumnsNumber={currentForm?.fixedColumnsNumber}
                    componentClasses={cx(styles.grid, {
                        [styles.subFormGrid]: isSubForm
                    })}
                    data={records}
                    rowsCount={pageRowsCount}
                    headerHeight={headerHeight}
                    rowHeight={rowHeight}
                    searchModeLevel={isParentInSearchMode ? 1 : searchMode}
                    columns={columns}
                    headerRenderer={headerRenderer}
                    cellRenderer={cellRenderer}
                    cellClasses={cx_menu_input_anchor}
                    onFocusCell={onFocusCell}
                    onReady={onReady}
                    onDestroy={onDestroy}
                    activeCellIndex={currentForm?.currentColumn.index}
                    activeRowIndex={currentForm?.currentRow}
                    validateFocusChange={beforeFocusCellChange}
                    onTableBodyClick={onTableBodyClick}
                    onCellResizeEnd={onCellResizeEnd}
                    onScroll={onScroll}
                    onKeyDown={onTableKeydown}
                    defaultScrollOffsetLeft={currentForm.tableScrollOffset.left}
                    defaultScrollOffsetTop={currentForm.tableScrollOffset.top}
                    isFormEmpty={isEmptyForm}
                    keyboardNavigationHandler={onKeyboardNavigation}
                    emptyStateRenderer={emptyStateRenderer}
                    onMouseWheel={onMouseWheel}
                />
            </Splitter>
        );
    }
);
